(function jamilaBeautyQuizModule(root) {
  'use strict';

  const CORE_STEP_IDS = [
    'primary',
    'secondary',
    'skin-type',
    'severity',
    'reactivity',
    'actives',
    'condition',
    'retinoid-safety',
  ];

  const STEP_ORDER = ['cleanse', 'treat', 'moisturize', 'protect'];

  const STEP_COLLECTIONS = {
    cleanse: '/collections/cleansers',
    treat: '/collections/skin-concerns',
    moisturize: '/collections/skincare',
    protect: '/collections/sunscreen',
  };

  const PRODUCT_RULES = {
    'cosrx-low-ph-good-morning-gel-cleanser': {
      step: 'cleanse', gentle: true, base: 25,
    },
    'cosrx-salicylic-acid-daily-gentle-cleanser': {
      step: 'cleanse', concerns: ['breakouts', 'large-pores'], skinTypes: ['oily', 'combination'], strong: true, base: 20,
    },
    'anua-niacinamide-10-txa-4-serum': {
      step: 'treat', concerns: ['breakouts', 'dark-spots', 'large-pores'], gentle: true, base: 20,
    },
    'skin1004-madagascar-centella-asiatica-100-ampoule': {
      step: 'treat', concerns: ['dryness', 'sensitivity'], gentle: true, base: 20, supportiveFallback: 10,
    },
    'eqqualberry-bakuchiol-plumping-serum': {
      step: 'treat', concerns: ['fine-lines'], gentle: true, base: 20, supportiveFallback: 4,
    },
    'eqqualberry-nad-peptide-boosting-serum': {
      step: 'treat', concerns: ['fine-lines', 'dryness'], gentle: true, base: 18, supportiveFallback: 3,
    },
    'celimax-the-vita-a-retinal-shot-tightening-booster': {
      step: 'treat', concerns: ['fine-lines', 'large-pores'], retinoid: true, strong: true, needsTolerance: true, base: 24,
    },
    'celimax-the-vita-a-retinol-shot-tightening-serum': {
      step: 'treat', concerns: ['fine-lines', 'large-pores'], retinoid: true, strong: true, needsTolerance: true, base: 20,
    },
    'axis-y-vegan-collagen-eye-serum': {
      step: 'treat', concerns: ['under-eye'], gentle: true, base: 25,
    },
    'dr-althea-147-barrier-cream': {
      step: 'moisturize', concerns: ['dryness'], skinTypes: ['dry'], gentle: true, base: 22,
    },
    'dr-althea-345-relief-cream': {
      step: 'moisturize', concerns: ['sensitivity', 'breakouts'], gentle: true, base: 22,
    },
    'medicube-hyaluronic-moisturizing-capsule-cream': {
      step: 'moisturize', concerns: ['dryness'], gentle: true, base: 18,
    },
    'medicube-pdrn-pink-collagen-capsule-cream': {
      step: 'moisturize', concerns: ['fine-lines'], skinTypes: ['normal', 'combination'], gentle: true, base: 20,
    },
    'anua-heartleaf-silky-moisture-mild-sunscreen': {
      step: 'protect', gentle: true, base: 100,
    },
  };

  const PRODUCT_COPY = {
    'cosrx-low-ph-good-morning-gel-cleanser': {
      reason: 'A gentle daily cleanse that supports a comfortable skin barrier.',
      usage: 'Massage onto damp skin, rinse, and follow the product label.',
    },
    'cosrx-salicylic-acid-daily-gentle-cleanser': {
      reason: 'Selected for oily or congested skin when no sensitivity or active-stacking risk is present.',
      usage: 'Use as directed; reduce frequency if dryness or irritation develops.',
    },
    'anua-niacinamide-10-txa-4-serum': {
      reason: 'Selected to support the look of blemish marks, uneven tone, or visible pores.',
      usage: 'Apply after cleansing and follow the product label.',
    },
    'skin1004-madagascar-centella-asiatica-100-ampoule': {
      reason: 'A gentle, hydration-focused option for dry or reactive-feeling skin.',
      usage: 'Apply after cleansing, then seal with moisturizer.',
    },
    'eqqualberry-bakuchiol-plumping-serum': {
      reason: 'A non-retinoid option selected for a gentle fine-line routine.',
      usage: 'Apply after cleansing and introduce gradually.',
    },
    'eqqualberry-nad-peptide-boosting-serum': {
      reason: 'Selected to support hydrated, smoother-looking skin without adding a retinoid.',
      usage: 'Apply after cleansing and follow the product label.',
    },
    'celimax-the-vita-a-retinal-shot-tightening-booster': {
      reason: 'Selected only for a previously tolerant, unrestricted fine-line or pore routine.',
      usage: 'Start 2 nights a week, then increase only if comfortable and follow the product label.',
    },
    'celimax-the-vita-a-retinol-shot-tightening-serum': {
      reason: 'A retinoid alternative reserved for a previously tolerant and unrestricted routine.',
      usage: 'Start 2 nights a week, then increase only if comfortable and follow the product label.',
    },
    'axis-y-vegan-collagen-eye-serum': {
      reason: 'Selected to support hydration and smoother-looking skin around the eyes.',
      usage: 'Use a small amount around the eye area as directed on the label.',
    },
    'dr-althea-147-barrier-cream': {
      reason: 'Selected to support moisture and the skin barrier for dry-feeling skin.',
      usage: 'Apply after treatment to seal in moisture.',
    },
    'dr-althea-345-relief-cream': {
      reason: 'A calming moisturizer choice for blemish-prone or reactive-feeling skin.',
      usage: 'Apply after treatment and stop if significant irritation develops.',
    },
    'medicube-hyaluronic-moisturizing-capsule-cream': {
      reason: 'A hydration-focused alternative for dry or dehydrated-feeling skin.',
      usage: 'Apply after treatment to help seal in hydration.',
    },
    'medicube-pdrn-pink-collagen-capsule-cream': {
      reason: 'Selected to support moisturized, plumper-looking skin.',
      usage: 'Apply after treatment and follow the product label.',
    },
    'anua-heartleaf-silky-moisture-mild-sunscreen': {
      reason: 'Daily sun protection completes every morning routine.',
      usage: 'Apply every morning as the final skincare step and reapply as directed.',
    },
  };

  const CONCERN_LABELS = {
    breakouts: 'breakouts and blemishes',
    'dark-spots': 'dark spots and uneven tone',
    dryness: 'dryness and dehydration',
    sensitivity: 'sensitivity and redness',
    'fine-lines': 'fine lines and firmness',
    'large-pores': 'visible or congested pores',
    'under-eye': 'under-eye hydration and fine lines',
  };

  function getActiveSet(answers) {
    return new Set(Array.isArray(answers.actives) ? answers.actives : []);
  }

  function hasStackingRisk(answers) {
    const activeSet = getActiveSet(answers);
    return ['retinoid', 'acids', 'benzoyl-peroxide', 'prescription'].some((value) => activeSet.has(value));
  }

  function isRetinoidQuestionRelevant(answers) {
    const concerns = [answers.primary, answers.secondary];
    const condition = answers.condition || 'none';
    const safety = answers.retinoidSafety || '';

    if (!concerns.some((value) => value === 'fine-lines' || value === 'large-pores')) return false;
    if (safety === 'yes' || safety === 'avoid') return false;
    if (answers.reactivity === 'often') return false;
    if (['painful-scarring', 'persistent-rash'].includes(answers.severity)) return false;
    if (!['none', 'not-sure'].includes(condition)) return false;
    if (hasStackingRisk(answers)) return false;
    return true;
  }

  function getVisibleStepIds(answers) {
    const ids = [...CORE_STEP_IDS];
    if (isRetinoidQuestionRelevant(answers)) ids.splice(ids.indexOf('actives') + 1, 0, 'retinoid-tolerance');
    return ids;
  }

  function getSafetyFlags(answers) {
    const activeSet = getActiveSet(answers);
    const condition = answers.condition || 'none';
    const medicalEscalation = ['painful-scarring', 'persistent-rash'].includes(answers.severity);
    const diagnosed = !['none', 'not-sure'].includes(condition);
    const clinicianGuidance = medicalEscalation || activeSet.has('prescription') || diagnosed;
    const gentleOnly = medicalEscalation || answers.reactivity === 'often' || clinicianGuidance;
    const stackingRisk = hasStackingRisk(answers);
    const retinoidExcluded = answers.retinoidSafety !== 'no'
      || gentleOnly
      || stackingRisk
      || answers.retinoidTolerance !== 'tolerated';

    return {
      medicalEscalation,
      clinicianGuidance,
      gentleOnly,
      stackingRisk,
      retinoidExcluded,
      pregnancyGuidance: answers.retinoidSafety === 'yes',
    };
  }

  function isEligible(rule, flags) {
    if (rule.retinoid && flags.retinoidExcluded) return false;
    if (rule.strong && (flags.gentleOnly || flags.stackingRisk)) return false;
    return true;
  }

  function score(rule, answers) {
    const primaryMatch = Boolean(rule.concerns && rule.concerns.includes(answers.primary));
    const secondaryMatch = Boolean(
      answers.secondary
      && answers.secondary !== 'none'
      && rule.concerns
      && rule.concerns.includes(answers.secondary),
    );

    let value = rule.step === 'treat' && !primaryMatch && !secondaryMatch
      ? (rule.supportiveFallback || 0)
      : (rule.base || 0);

    if (primaryMatch) value += 100;
    if (secondaryMatch) value += 30;
    if (rule.skinTypes && rule.skinTypes.includes(answers.skinType)) value += 20;
    if (answers.reactivity === 'often' && rule.gentle) value += 25;
    if (rule.needsTolerance && answers.retinoidTolerance === 'tolerated') value += 45;
    return value;
  }

  function selectStep(step, answers, products, flags, selectedHandles) {
    const candidates = Object.entries(PRODUCT_RULES)
      .map(([handle, rule], index) => ({ handle, rule, index, product: products[handle] }))
      .filter((candidate) => candidate.rule.step === step)
      .filter((candidate) => candidate.product && candidate.product.available)
      .filter((candidate) => !selectedHandles.has(candidate.handle))
      .filter((candidate) => isEligible(candidate.rule, flags))
      .map((candidate) => ({ ...candidate, score: score(candidate.rule, answers) }))
      .filter((candidate) => candidate.score > 0)
      .sort((left, right) => right.score - left.score || left.index - right.index);

    if (!candidates.length) {
      return {
        step,
        status: 'unavailable',
        product: null,
        reason: 'A suitable match is not currently available.',
        usage: '',
        collectionUrl: STEP_COLLECTIONS[step],
      };
    }

    const selected = candidates[0];
    selectedHandles.add(selected.handle);
    return {
      step,
      status: 'available',
      product: selected.product,
      reason: PRODUCT_COPY[selected.handle].reason,
      usage: PRODUCT_COPY[selected.handle].usage,
      collectionUrl: STEP_COLLECTIONS[step],
      retinoid: Boolean(selected.rule.retinoid),
      strong: Boolean(selected.rule.strong),
    };
  }

  function buildProfile(answers) {
    const primary = CONCERN_LABELS[answers.primary] || 'your main skin goal';
    const secondary = answers.secondary && answers.secondary !== 'none'
      ? `, with added support for ${CONCERN_LABELS[answers.secondary] || answers.secondary}`
      : '';
    const skinType = answers.skinType ? `${answers.skinType} skin` : 'your skin';
    return `A four-step routine for ${skinType}, focused on ${primary}${secondary}.`;
  }

  function recommend(answers, products) {
    const flags = getSafetyFlags(answers);
    const selectedHandles = new Set();
    const steps = STEP_ORDER.map((step) => selectStep(step, answers, products, flags, selectedHandles));
    const treatment = steps.find((item) => item.step === 'treat');
    const morning = treatment && treatment.retinoid
      ? ['cleanse', 'moisturize', 'protect']
      : ['cleanse', 'treat', 'moisturize', 'protect'];

    return {
      flags,
      profile: buildProfile(answers),
      steps,
      morning,
      evening: ['cleanse', 'treat', 'moisturize'],
    };
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  function titleCaseStep(step) {
    return step.charAt(0).toUpperCase() + step.slice(1);
  }

  if (root && root.HTMLElement && root.customElements) {
    class JamilaBeautyQuiz extends root.HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.intro = this.querySelector('[data-quiz-intro]');
        this.form = this.querySelector('[data-quiz-form]');
        this.steps = [...this.querySelectorAll('[data-quiz-step]')];
        this.progress = this.querySelector('[data-quiz-progress]');
        this.progressBar = this.querySelector('[data-quiz-progress-bar]');
        this.count = this.querySelector('[data-quiz-count]');
        this.error = this.querySelector('[data-quiz-error]');
        this.backButton = this.querySelector('[data-quiz-back]');
        this.nextButton = this.querySelector('[data-quiz-next]');
        this.results = this.querySelector('[data-quiz-results]');
        this.status = this.querySelector('[data-quiz-status]');
        this.startButton = this.querySelector('[data-quiz-start]');
        this.currentIndex = 0;

        try {
          const records = JSON.parse(this.querySelector('[data-quiz-products]').textContent);
          this.products = Object.fromEntries(records.map((product) => [product.handle, product]));
        } catch (error) {
          this.products = {};
          this.status.textContent = 'Product data could not be loaded. Collection links will be shown instead.';
        }

        this.bindEvents();
        this.markSelectedOptions();
      }

      bindEvents() {
        this.addEventListener('click', (event) => {
          const start = event.target.closest('[data-quiz-start]');
          const back = event.target.closest('[data-quiz-back]');
          const next = event.target.closest('[data-quiz-next]');
          const retake = event.target.closest('[data-quiz-retake]');

          if (start) {
            this.intro.hidden = true;
            this.form.hidden = false;
            this.currentIndex = 0;
            this.syncSecondaryOptions();
            this.showStep(0);
            return;
          }

          if (back) {
            this.syncCurrentIndex();
            this.showStep(this.currentIndex - 1);
            return;
          }

          if (next) {
            this.syncCurrentIndex();
            if (!this.validateCurrentStep()) return;
            const visible = this.visibleSteps();
            if (this.currentIndex < visible.length - 1) this.showStep(this.currentIndex + 1);
            else this.renderResults();
            return;
          }

          if (retake) this.retakeQuiz();
        });

        this.form.addEventListener('change', (event) => {
          const input = event.target;
          if (!(input instanceof root.HTMLInputElement)) return;

          if (input.name === 'actives' && input.checked) {
            const activeInputs = [...this.form.querySelectorAll('input[name="actives"]')];
            if (input.hasAttribute('data-exclusive-active')) {
              activeInputs.forEach((candidate) => {
                if (candidate !== input) candidate.checked = false;
              });
            } else {
              activeInputs.filter((candidate) => candidate.hasAttribute('data-exclusive-active')).forEach((candidate) => {
                candidate.checked = false;
              });
            }
          }

          if (input.name === 'primary') this.syncSecondaryOptions();
          this.markSelectedOptions();
          this.syncCurrentIndex();
          this.error.hidden = true;
        });
      }

      collectAnswers() {
        const selectedValue = (name) => {
          const selected = this.form.querySelector(`input[name="${name}"]:checked`);
          return selected ? selected.value : '';
        };

        return {
          primary: selectedValue('primary'),
          secondary: selectedValue('secondary'),
          skinType: selectedValue('skinType'),
          severity: selectedValue('severity'),
          reactivity: selectedValue('reactivity'),
          actives: [...this.form.querySelectorAll('input[name="actives"]:checked')].map((input) => input.value),
          condition: selectedValue('condition'),
          retinoidSafety: selectedValue('retinoidSafety'),
          retinoidTolerance: selectedValue('retinoidTolerance'),
        };
      }

      visibleSteps() {
        return getVisibleStepIds(this.collectAnswers())
          .map((id) => this.querySelector(`[data-quiz-step="${id}"]`))
          .filter(Boolean);
      }

      syncCurrentIndex() {
        const activeStep = this.steps.find((step) => !step.hidden);
        const visibleIndex = this.visibleSteps().indexOf(activeStep);
        if (visibleIndex >= 0) this.currentIndex = visibleIndex;
      }

      syncSecondaryOptions() {
        const primary = this.form.querySelector('input[name="primary"]:checked');
        const selectedPrimary = primary ? primary.value : '';

        this.querySelectorAll('[data-secondary-value]').forEach((option) => {
          const input = option.querySelector('input');
          const shouldHide = option.dataset.secondaryValue === selectedPrimary;
          option.hidden = shouldHide;
          input.disabled = shouldHide;
          if (shouldHide) input.checked = false;
        });
        this.markSelectedOptions();
      }

      markSelectedOptions() {
        this.querySelectorAll('.jamila-quiz-option').forEach((option) => {
          const input = option.querySelector('input');
          option.classList.toggle('is-selected', Boolean(input && input.checked));
        });
      }

      validateCurrentStep() {
        const step = this.visibleSteps()[this.currentIndex];
        if (!step) return false;
        const inputs = [...step.querySelectorAll('input:not(:disabled)')];
        const valid = inputs.some((input) => input.checked);
        this.error.hidden = valid;
        if (!valid && inputs[0]) inputs[0].focus();
        return valid;
      }

      showStep(index) {
        const visible = this.visibleSteps();
        this.currentIndex = Math.max(0, Math.min(index, visible.length - 1));
        this.steps.forEach((step) => { step.hidden = true; });
        const activeStep = visible[this.currentIndex];
        if (!activeStep) return;
        activeStep.hidden = false;

        const current = this.currentIndex + 1;
        const total = visible.length;
        const progressValue = `${(current / total) * 100}%`;
        this.progress.setAttribute('aria-valuenow', String(current));
        this.progress.setAttribute('aria-valuemax', String(total));
        this.progress.setAttribute('aria-valuetext', `Question ${current} of ${total}`);
        this.progressBar.style.setProperty('--quiz-progress', progressValue);
        this.progress.style.setProperty('--quiz-progress', progressValue);
        this.count.textContent = `Question ${current} of ${total}`;
        this.backButton.hidden = this.currentIndex === 0;
        this.nextButton.textContent = this.currentIndex === total - 1 ? 'See My Routine' : 'Continue';
        this.error.hidden = true;
        this.status.textContent = `Question ${current} of ${total}`;
        this.focusQuestion();
      }

      focusQuestion() {
        const activeStep = this.visibleSteps()[this.currentIndex];
        const legend = activeStep && activeStep.querySelector('legend');
        if (!legend) return;
        const schedule = root.requestAnimationFrame || ((callback) => root.setTimeout(callback, 0));
        schedule(() => legend.focus({ preventScroll: true }));
      }

      createSchedule(title, steps) {
        const section = createElement('section', 'jamila-quiz-schedule');
        section.append(createElement('h3', '', title));
        const list = createElement('ol');
        steps.forEach((step) => list.append(createElement('li', '', titleCaseStep(step))));
        section.append(list);
        return section;
      }

      createProductCard(item) {
        const card = createElement('article', `jamila-quiz-product${item.status === 'unavailable' ? ' is-unavailable' : ''}`);
        const stepLabel = createElement('span', 'jamila-quiz-product-step', titleCaseStep(item.step));

        if (item.status === 'available') {
          const media = createElement('div', 'jamila-quiz-product-media');
          media.append(stepLabel);
          if (item.product.image) {
            const image = createElement('img');
            image.src = item.product.image;
            image.alt = item.product.title;
            image.loading = 'lazy';
            image.width = 900;
            image.height = 900;
            media.append(image);
          }
          card.append(media);

          const copy = createElement('div', 'jamila-quiz-product-copy');
          copy.append(createElement('h3', '', item.product.title));
          copy.append(createElement('p', 'jamila-quiz-product-price', item.product.price));
          copy.append(createElement('p', 'jamila-quiz-product-reason', item.reason));
          copy.append(createElement('p', 'jamila-quiz-product-usage', item.usage));
          const link = createElement('a', 'jamila-quiz-product-link', 'View Product');
          link.href = item.product.url;
          copy.append(link);
          card.append(copy);
          return card;
        }

        const copy = createElement('div', 'jamila-quiz-product-copy');
        copy.append(stepLabel);
        copy.append(createElement('h3', '', `No ${titleCaseStep(item.step)} match in stock`));
        copy.append(createElement('p', 'jamila-quiz-product-reason', item.reason));
        const link = createElement('a', 'jamila-quiz-product-link', 'Browse Alternatives');
        link.href = item.collectionUrl;
        copy.append(link);
        card.append(copy);
        return card;
      }

      createGuidance(flags, answers) {
        const stack = createElement('div', 'jamila-quiz-guidance-stack');
        if (flags.clinicianGuidance) {
          const medicalCopy = this.dataset.medicalCopy
            || "Painful, scarring, rapidly worsening, or persistently irritated skin deserves a dermatologist's assessment. This gentle routine is supportive care only.";
          stack.append(createElement('p', 'jamila-quiz-guidance is-medical', medicalCopy));
        }
        if (flags.pregnancyGuidance) {
          stack.append(createElement('p', 'jamila-quiz-guidance', 'Retinoids were excluded. Please review every product and ingredient list with your obstetrician or dermatologist.'));
        } else if (answers.retinoidSafety === 'avoid') {
          stack.append(createElement('p', 'jamila-quiz-guidance', 'Retinol and retinal were excluded from this routine as requested.'));
        }
        if (flags.stackingRisk) {
          stack.append(createElement('p', 'jamila-quiz-guidance', 'Your routine avoids adding another strong active alongside the treatments you already use.'));
        }
        return stack;
      }

      renderResults() {
        const answers = this.collectAnswers();
        const recommendation = recommend(answers, this.products);
        this.form.hidden = true;
        this.results.replaceChildren();

        const header = createElement('header', 'jamila-quiz-results-header');
        header.append(createElement('p', 'jamila-quiz-results-kicker', 'YOUR PERSONALIZED ROUTINE'));
        const heading = createElement('h2', '', 'Your Jamila Routine');
        heading.tabIndex = -1;
        header.append(heading);
        header.append(createElement('p', 'jamila-quiz-profile', recommendation.profile));
        this.results.append(header);

        const guidance = this.createGuidance(recommendation.flags, answers);
        if (guidance.childElementCount) this.results.append(guidance);

        const schedules = createElement('div', 'jamila-quiz-schedules');
        schedules.append(this.createSchedule('Morning Routine', recommendation.morning));
        schedules.append(this.createSchedule('Evening Routine', recommendation.evening));
        this.results.append(schedules);
        this.results.append(createElement('h3', 'jamila-quiz-results-heading', 'Your four-step routine'));

        const grid = createElement('div', 'jamila-quiz-results-grid');
        recommendation.steps.forEach((item) => grid.append(this.createProductCard(item)));
        this.results.append(grid);

        this.results.append(createElement(
          'p',
          'jamila-quiz-aftercare',
          'Patch-test each new product on a small area for 7–10 days, introduce one product at a time, and stop if significant irritation occurs. Always follow the current product label.',
        ));

        const actions = createElement('div', 'jamila-quiz-result-actions');
        const retake = createElement('button', 'jamila-quiz-secondary', 'Retake Quiz');
        retake.type = 'button';
        retake.setAttribute('data-quiz-retake', '');
        actions.append(retake);
        const contact = createElement('a', 'jamila-quiz-primary', 'Ask Jamila for Help');
        contact.href = this.dataset.contactUrl || '/pages/contact-us';
        actions.append(contact);
        this.results.append(actions);

        this.results.hidden = false;
        this.status.textContent = 'Your personalized four-step Jamila routine is ready.';
        heading.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }

      retakeQuiz() {
        this.form.reset();
        this.results.replaceChildren();
        this.results.hidden = true;
        this.form.hidden = true;
        this.intro.hidden = false;
        this.currentIndex = 0;
        this.steps.forEach((step, index) => { step.hidden = index !== 0; });
        this.markSelectedOptions();
        this.syncSecondaryOptions();
        this.status.textContent = 'Quiz reset.';
        this.startButton.focus({ preventScroll: true });
      }
    }

    if (!root.customElements.get('jamila-beauty-quiz')) {
      root.customElements.define('jamila-beauty-quiz', JamilaBeautyQuiz);
    }
  }

  const JamilaBeautyQuizEngine = {
    PRODUCT_RULES,
    PRODUCT_COPY,
    isRetinoidQuestionRelevant,
    getVisibleStepIds,
    getSafetyFlags,
    recommend,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = JamilaBeautyQuizEngine;
  if (root) root.JamilaBeautyQuizEngine = JamilaBeautyQuizEngine;
}(typeof window !== 'undefined' ? window : globalThis));
